import {Toaster} from "@/components/ui/toaster";
import {Toaster as Sonner} from "@/components/ui/sonner";
import {TooltipProvider} from "@/components/ui/tooltip";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import {MuiThemeProvider, ThemeContextProvider} from "./theme/mui-theme";
import AppLayout from "./components/layout/AppLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import NotFound from "./pages/NotFound";
import React, {useEffect} from "react";
import {store} from "./service/store";
import {CircularProgress} from "@mui/material";
import {useSignalEffect} from "@preact/signals-react";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  // With useSignalEffect, this component will automatically re-render when the signal changes
  const [isAuthenticated, setIsAuthenticated] = React.useState(store.technician.data.value !== null);

  useSignalEffect(() => {
    setIsAuthenticated(store.technician.data.value !== null);
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  // Use React state that will be updated by useSignalEffect
  const [isLoading, setIsLoading] = React.useState(store.technician.isLoading.value);

  // This will automatically update when the isLoading signal changes
  useSignalEffect(() => {
    setIsLoading(store.technician.isLoading.value);
  });

  useEffect(() => {
    const technicianName = localStorage.getItem("technician");
    if (technicianName && !store.technician.data.value) {
      store.technician.load();
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <MuiThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  {/*<Route path="devices" element={<Devices />} />*/}
                  <Route path="customers" element={<Customers />} />
                  <Route path="customers/:id" element={<CustomerDetail />} />
                  {/*<Route path="history" element={<History />} />*/}
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </MuiThemeProvider>
      </ThemeContextProvider>
    </QueryClientProvider>
  );
};

export default App;
